import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { FirebaseAnalytics } from "@ionic-native/firebase-analytics";
import { IonIcon } from "@ionic/react";
import { checkmarkCircleOutline } from "ionicons/icons";

import PageLayout from "../../layouts/PageLayout";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Title from "components/Title";
import Button from "components/Button";
import { Subscription } from "model";
import api from "../../api";
import { tryCatch } from "../../utils";

import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";

interface Props {}

export const PurchaseContainer: React.FC<Props> = () => {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const history = useHistory();
  const { t } = useTranslation("purchase");
  const [stripeKey, setStripeKey] = useState<any>(null);
  const [stripePromise, setStripePromise] = useState<any>(null);
  const [clientSecret, setClientSecret] = useState<string>("");
  const [formStep, setFormStep] = useState<number>(1);
  const [prices, setPrices] = useState<any>([]);
  const { subscriptions } = useSelector((state: RootState) => ({
    subscriptions: state.adapter.subscriptions,
  }));
  const [purchaseUnlocked, setPurchaseUnlocked] = useState<boolean>(false);

  useEffect(() => {
    FirebaseAnalytics.logEvent("init_page", { page: "purchase" });
    getConfiguration();
  }, []);

  useEffect(() => {
    if (stripeKey) {
      let newStripePromise = loadStripe(stripeKey);
      setStripePromise(newStripePromise);
    }
  }, [stripeKey]);

  useEffect(() => {
    let subsWithPaymentMethod = subscriptions.filter(
      (sub: Subscription) => sub.default_payment_method
    );
    if (subsWithPaymentMethod.length > 0) {
      setPurchaseUnlocked(true);
    } else {
      setPurchaseUnlocked(false);
    }
  }, [subscriptions]);

  const getConfiguration = async () => {
    const configRes = await tryCatch(api.subscription.getConfig());
    if (configRes.error) {
      console.warn(configRes.error.message);
    } else {
      if (configRes.result.data.publishable_key) {
        setStripeKey(configRes.result.data.publishable_key);
      }
      if (configRes.result.data.prices) {
        setPrices(configRes.result.data.prices);
      }
    }
  };

  const getCheckoutSession = async (price_id: string) => {
    setClientSecret("");

    const apiResult = await tryCatch(
      api.subscription.createOneTimePaymentCheckoutSession(
        price_id,
        subscriptionId
      )
    );

    if (apiResult.error) {
      console.warn(apiResult.error.message);
    } else {
      const client_secret = apiResult.result.data.client_secret;
      console.log("RESULT: ", client_secret);
      setClientSecret(apiResult.result.data.client_secret);
    }
  };

  const handleCheckoutReady = (price_id: string) => {
    if (stripePromise) {
      setFormStep(2);
      getCheckoutSession(price_id);
    } else {
      console.log(
        "cannot continue, no selected product or stripe promise failed to load"
      );
    }
  };

  return (
    <PageLayout back={history.goBack}>
      <Title title={t("title")} className="mb-2" />
      {!purchaseUnlocked && (
        <div
          style={{
            padding: "20px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <p style={{ whiteSpace: "pre-line", width: "80%" }}>
            {t("purchaseLocked")}
          </p>
        </div>
      )}
      {purchaseUnlocked && prices && prices.length > 0 && stripePromise && (
        <div>
          {formStep === 1 && (
            <div style={styles.formContainer}>
              <div style={styles.productContainer}>
                {prices
                  .filter(
                    (price: any) =>
                      !price.recurring &&
                      price.metadata &&
                      "INCLUDED_CODE" in price.metadata
                  )
                  .map((price: any, i: number) => {
                    return (
                      <div
                        key={`${price.id}_${Date.now()}`}
                        style={styles.productCardContainer}
                      >
                        <div style={styles.productCard}>
                          <div style={styles.productTitle}>
                            {price.product.name}
                          </div>
                          <div style={styles.productSubTitle}>
                            {price.product.description}
                          </div>
                          <div style={styles.productPriceContainer}>
                            <div style={styles.productPrice}>{`$${Number(
                              price.unit_amount / 100
                            ).toFixed(2)}`}</div>
                          </div>
                          <div style={{ marginBottom: "20px", width: "100%" }}>
                            <Button
                              data-testid="submit"
                              className="text-center mr-3 w-full"
                              size="small"
                              type="button"
                              style={{ width: "100%" }}
                              onClick={() => {
                                handleCheckoutReady(price.id);
                              }}
                            >
                              {t("purchase")}
                            </Button>
                          </div>
                          {price.product.features.length > 0 && (
                            <div style={{ width: "100%" }}>
                              <div
                                style={{
                                  ...styles.featureListItem,
                                  marginBottom: "10px",
                                }}
                              >
                                This includes:
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "5px",
                                }}
                              >
                                {price.product.features.map(
                                  (feature: any, i: number) => (
                                    <div
                                      key={`${i}_${Date.now()}`}
                                      style={{ display: "flex", gap: "5px" }}
                                    >
                                      <div>
                                        <IonIcon
                                          icon={checkmarkCircleOutline}
                                          style={{
                                            color: "rgba(26,26,26,0.5)",
                                          }}
                                          aria-hidden="true"
                                        />
                                      </div>
                                      <div style={styles.featureListItem}>
                                        {feature.name}
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
          {formStep === 2 && clientSecret && (
            <div style={styles.formContainer}>
              <div style={{ alignSelf: "flex-start" }}>
                <Button
                  data-testid="submit"
                  className="text-center mr-3"
                  size="small"
                  type="button"
                  disabled={false}
                  onClick={() => {
                    setFormStep(1);
                  }}
                >
                  Back
                </Button>
              </div>
              {clientSecret && (
                <EmbeddedCheckoutProvider
                  stripe={stripePromise}
                  options={{ clientSecret }}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              )}
            </div>
          )}
        </div>
      )}
    </PageLayout>
  );
};

const styles = {
  formContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column" as "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    gap: "10px",
  },
  productContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row" as "row",
    flexWrap: "wrap" as "wrap",
    justifyContent: "center",
    gap: "10px",
  },
  productCardContainer: {
    width: "290px",
    height: "450px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  productCard: {
    width: "270px",
    height: "450px",
    padding: "10px",
    display: "flex",
    flexDirection: "column" as "column",
    justifyContent: "flex-start",
    alignItems: "center",
    transition: "0.4s",
    cursor: "pointer",
  },
  productTitle: {
    fontSize: "18px",
    fontWeight: 500,
    margin: "5px",
    width: "100%",
    marginBottom: "10px",
  },
  productSubTitle: {
    fontSize: "12px",
    width: "100%",
    color: "rgba(26,26,26,0.5)",
    height: "24px",
    marginBottom: "20px",
  },
  productPriceContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "5px",
    marginBottom: "20px",
  },
  productPrice: {
    fontWeight: 700,
    fontSize: "32px",
  },
  featureListItem: {
    fontSize: "14px",
  },
};

export default PurchaseContainer;
